import React from 'react';
import {
  Box, Container, Typography, Button, Grid, Stack, Chip, Card, CardContent,
} from '@mui/material';
import {
  CheckCircle, ArrowForward, Security, Star,
} from '@mui/icons-material';
import { C, Section } from './theme';

const terminalLines = [
  { color: '#64748B', text: '# Deploy your stack on Hosting Services VPS' },
  { color: '#2563EB', text: '$ hostingservices vps deploy --plan nvme4' },
  { color: '#059669', text: '► Provisioning 2 vCPU, 4GB DDR5 RAM...' },
  { color: '#059669', text: '► Allocating 100GB NVMe SSD storage' },
  { color: '#059669', text: '► Configuring firewall & DDoS protection...' },
  { color: '#059669', text: '► Setting up SSL certificate...' },
  { color: '#059669', text: '► Starting application server...' },
  { color: '#E2E8F0', text: ' ' },
  { color: '#059669', text: '✓ VPS deployed successfully' },
  { color: '#64748B', text: 'ℹ Dashboard: https://vps-7392.hostingservices.com' },
  { color: '#2563EB', text: '$ _' },
];

const heroFeatures = [
  'Scale up instantly on demand with allocated resources',
  'AMD EPYC · PCIe 5 RAM · NVMe Storage',
  'DDoS protection included + dedicated IP address',
  'Self-managed so you are in control',
];

const VpsHero = ({ onGetStarted }) => (
  <Section id="hero" sx={{ background: '#F8FAFC', borderBottom: `1px solid ${C.border}`, pt: { xs: 6, md: 10 }, pb: { xs: 7, md: 11 } }}>
    <Container maxWidth="lg">
      <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">

        {/* Left Column: Hero Text (MUI v6 Grid size) */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Chip label="VPS HOSTING" size="small" sx={{
            background: C.primaryLight,
            color: C.primary,
            fontWeight: 800,
            letterSpacing: '0.12em',
            fontSize: '0.72rem',
            mb: 2.5,
            border: `1px solid ${C.primary}30`,
            borderRadius: '6px',
          }} />

          <Typography variant="h1" sx={{
            fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.3rem', lg: '3.6rem' },
            fontWeight: 900,
            lineHeight: 1.12,
            color: C.textPrimary,
            mb: 2.5,
            letterSpacing: '-0.035em',
          }}>
            Get full control with a{' '}
            <Box component="span" sx={{ color: C.primary }}>
              virtual private server
            </Box>
          </Typography>

          <Typography sx={{
            color: C.textSecondary,
            fontSize: { xs: '1rem', md: '1.1rem' },
            lineHeight: 1.7,
            mb: 3.5,
            maxWidth: 540,
          }}>
            Absolute freedom on your self-managed virtual private server with full root
            access, NVMe SSD speed, effortless upscaling, and flat rate pricing.
          </Typography>

          {/* Checklist */}
          <Stack spacing={1.5} sx={{ mb: 4 }}>
            {heroFeatures.map((f) => (
              <Stack key={f} direction="row" spacing={1.5} alignItems="flex-start">
                <CheckCircle sx={{ color: C.success, fontSize: 20, mt: '2px', flexShrink: 0 }} />
                <Typography sx={{ color: C.textPrimary, fontSize: '0.95rem', fontWeight: 500 }}>
                  {f}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Action buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={onGetStarted}
              sx={{
                background: C.primary,
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '1rem',
                px: 4,
                py: 1.5,
                borderRadius: '10px',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  background: C.primaryHover,
                  boxShadow: '0 8px 20px rgba(37,99,235,0.25)',
                },
              }}
            >
              View VPS Plans
            </Button>

            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: C.border,
                color: C.textPrimary,
                fontWeight: 600,
                fontSize: '1rem',
                px: 4,
                py: 1.5,
                borderRadius: '10px',
                textTransform: 'none',
                background: '#FFFFFF',
                '&:hover': {
                  borderColor: C.primary,
                  color: C.primary,
                  background: C.primaryLight,
                },
              }}
            >
              See Managed VPS
            </Button>
          </Stack>

          <Typography sx={{ color: C.textMuted, fontSize: '0.84rem' }}>
            Not a developer?{' '}
            <Box component="span" sx={{ color: C.primary, fontWeight: 700, cursor: 'pointer' }}>
              See Managed VPS
            </Box>{' '}
            — we run the server for you
          </Typography>
        </Grid>

        {/* Right Column: Terminal & Stats (MUI v6 Grid size) */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card elevation={0} sx={{
            border: `1px solid ${C.border}`,
            borderRadius: '16px',
            overflow: 'hidden',
            background: '#FFFFFF',
            boxShadow: '0 20px 40px rgba(15,23,42,0.06)',
          }}>
            {/* Terminal Top Bar */}
            <Box sx={{
              px: 2.5, py: 1.25,
              background: '#0F172A',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              {['#EF4444', '#F59E0B', '#10B981'].map((color) => (
                <Box key={color} sx={{ width: 11, height: 11, borderRadius: '50%', background: color }} />
              ))}
              <Typography sx={{
                color: '#94A3B8', fontSize: '0.78rem', mx: 'auto',
                fontFamily: 'monospace', fontWeight: 600,
              }}>
                terminal — vps-deploy
              </Typography>
            </Box>

            {/* URL Security Strip */}
            <Box sx={{
              px: 2.5, py: 0.9,
              background: '#1E293B',
              borderBottom: '1px solid #334155',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <Security sx={{ color: '#10B981', fontSize: 14 }} />
              <Typography sx={{ color: '#CBD5E1', fontSize: '0.76rem', fontFamily: 'monospace' }}>
                🔒 vps-7392.hostingservices.com/dashboard
              </Typography>
            </Box>

            {/* Terminal Body */}
            <CardContent sx={{ p: 3, background: '#090D16', fontFamily: '"Fira Code", monospace', fontSize: '0.82rem' }}>
              {terminalLines.map((line, idx) => (
                <Box key={idx} sx={{ mb: 0.5 }}>
                  <Typography component="span" sx={{ color: line.color, fontFamily: 'inherit', fontSize: 'inherit' }}>
                    {line.text}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Customer Rating Strip */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={0.5}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} sx={{ color: '#F59E0B', fontSize: 18 }} />
              ))}
            </Stack>
            <Typography sx={{ color: C.textSecondary, fontSize: '0.88rem' }}>
              <strong style={{ color: C.textPrimary }}>Excellent 4.5</strong> out of 5 based on <strong>31,046 reviews</strong>
            </Typography>
          </Stack>
        </Grid>

      </Grid>
    </Container>
  </Section>
);

export default VpsHero;
